import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, X, TrendingDown, TrendingUp, ShoppingCart, PackageX, Package, RefreshCw, Sparkles, Edit3, MessageSquareText, UserPlus } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// Map notification type → icon + short human-readable label
const TYPE_ICON = {
  WATCH_PRICE_DROP: TrendingDown,
  WATCH_PRICE_INCREASE: TrendingUp,
  WATCH_STATUS_CHANGE: RefreshCw,
  WATCH_BACK_IN_STOCK: Package,
  WATCH_LOW_STOCK: ShoppingCart,
  WATCH_POST_UPDATED: Edit3,
  WATCH_POST_DELETED: PackageX,
  // iter79 Batch 2: Marketplace Negotiation
  MP_NEGOTIATION_NEW: MessageSquareText,
  MP_NEGOTIATION_COUNTER: MessageSquareText,
  MP_NEGOTIATION_ACCEPTED: Sparkles,
  MP_NEGOTIATION_REJECTED: PackageX,
  MP_NEGOTIATION_BUYER_ACCEPTED: Sparkles,
  MP_NEGOTIATION_BUYER_DECLINED: PackageX,
  // Bestell-/Lifecycle-Events (Header-Glocke)
  NEW_ORDER: ShoppingCart,
  ORDER_RECEIVED: Package,
  ORDER_PAID: ShoppingCart,
  ORDER_SHIPPED: Package,
  ORDER_CONTENT_READY: Sparkles,
  ORDER_CONTENT_UPLOAD_REQUIRED: Edit3,
  ORDER_CONTENT_EXPIRY_SOON: RefreshCw,
  WORN_VIDEO_READY: Sparkles,
  WORN_VIDEO_UPLOAD_REQUIRED: Edit3,
  STX_DIGITAL_AVAILABLE: Sparkles,
  FOLLOW_REQUEST: UserPlus,
};
const TYPE_LABEL_KEY = {
  WATCH_PRICE_DROP: "notifications.type_price_drop",
  WATCH_PRICE_INCREASE: "notifications.type_price_increase",
  WATCH_STATUS_CHANGE: "notifications.type_status_change",
  WATCH_BACK_IN_STOCK: "notifications.type_back_in_stock",
  WATCH_LOW_STOCK: "notifications.type_low_stock",
  WATCH_POST_UPDATED: "notifications.type_post_updated",
  WATCH_POST_DELETED: "notifications.type_post_deleted",
  MP_NEGOTIATION_NEW: "notifications.type_negotiation_new",
  MP_NEGOTIATION_COUNTER: "notifications.type_negotiation_counter",
  MP_NEGOTIATION_ACCEPTED: "notifications.type_negotiation_accepted",
  MP_NEGOTIATION_REJECTED: "notifications.type_negotiation_rejected",
  MP_NEGOTIATION_BUYER_ACCEPTED: "notifications.type_negotiation_buyer_accepted",
  MP_NEGOTIATION_BUYER_DECLINED: "notifications.type_negotiation_buyer_declined",
  FOLLOW_REQUEST: "notifications.type_follow_request",
};

function relativeTimeT(iso, t) {
  if (!iso) return "";
  const d = new Date(iso);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return t("notifications.relative_now");
  if (diffSec < 3600) return t("notifications.relative_min", { n: Math.floor(diffSec / 60) });
  if (diffSec < 86400) return t("notifications.relative_hour", { n: Math.floor(diffSec / 3600) });
  const days = Math.floor(diffSec / 86400);
  return days === 1 ? t("notifications.relative_day_one") : t("notifications.relative_day_other", { n: days });
}

export default function NotificationsDropdown({ onClose, unreadCount, setUnreadCount }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  const load = useCallback(async () => {
    if (!user || user === false) return;
    setLoading(true);
    try {
      // iter43: Dropdown zeigt NUR ungelesene Notifs – so bleibt es immer eine
      // "Neu"-Übersicht. Gelesene sind über die Watchlist erreichbar.
      const { data } = await api.get("/me/watch-notifications?limit=30&unread_only=true");
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose?.();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose]);

  const openItem = async (item) => {
    // iter43: Instant-remove aus der lokalen Liste, damit die Übersicht
    // sofort nur noch NEUE (ungelesene) Notifs enthält.
    setItems(prev => prev.filter(i => i.notification_id !== item.notification_id));
    try {
      await api.post(`/me/watch-notifications/mark-read/${item.target_id}`);
      window.dispatchEvent(new CustomEvent("watch-unread-refresh"));
      setUnreadCount?.(Math.max(0, (unreadCount || 0) - 1));
    } catch { /* ignore */ }
    onClose?.();
    navigate(item.link_path || "/watchlist");
  };

  const markAllRead = async () => {
    try {
      await api.post("/me/watch-notifications/mark-all-read");
      window.dispatchEvent(new CustomEvent("watch-unread-refresh"));
      setUnreadCount?.(0);
      setItems([]);
    } catch { /* ignore */ }
  };

  // iter169.13 · User-facing Delete (Einzeln + Alle)
  const deleteOne = async (e, item) => {
    e.stopPropagation();
    setItems(prev => prev.filter(i => i.notification_id !== item.notification_id));
    try {
      await api.delete(`/me/watch-notifications/${item.notification_id}`);
      if (!item.read) setUnreadCount?.(Math.max(0, (unreadCount || 0) - 1));
    } catch { /* silent */ }
  };
  const clearAll = async () => {
    if (!window.confirm(t("notifications.clear_all_confirm"))) return;
    setItems([]);
    setUnreadCount?.(0);
    try { await api.post("/me/watch-notifications/clear-all"); } catch { /* silent */ }
    window.dispatchEvent(new CustomEvent("watch-unread-refresh"));
  };

  return (
    <div
      ref={ref}
      data-testid="notifications-dropdown"
      className="absolute right-2 top-12 w-[92vw] max-w-sm bg-white border border-[#E5E5E5] shadow-xl z-[60] rounded overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] bg-[#FAFAFA]">
        <h2 className="text-sm font-bold text-[#0A0A0A]">{t("notifications.title")}</h2>
        <div className="flex items-center gap-2">
          {items.some(i => !i.read) && (
            <button data-testid="notif-mark-all-read" onClick={markAllRead}
              className="text-[10px] text-[#666] hover:text-[#0A0A0A] font-medium">
              {t("notifications.mark_all_read", { defaultValue: "Alle gelesen" })}
            </button>
          )}
          {items.length > 0 && (
            <button data-testid="notif-clear-all" onClick={clearAll}
              className="text-[10px] text-red-500 hover:text-red-700 font-medium">
              {t("notifications.clear_all_short", { defaultValue: "Alle löschen" })}
            </button>
          )}
          <button data-testid="notif-dropdown-close" onClick={onClose} className="text-[#666] hover:text-[#0A0A0A]">
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="max-h-[70vh] overflow-y-auto">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-5 h-5 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <Bell size={28} className="mx-auto text-[#E5E5E5] mb-2" />
            <p className="text-xs text-[#999]">{t("notifications.empty")}</p>
          </div>
        ) : (
          <div className="divide-y divide-[#F0F0F0]">
            {items.map((it) => {
              const Icon = TYPE_ICON[it.type] || Sparkles;
              const label = TYPE_LABEL_KEY[it.type]
                ? t(TYPE_LABEL_KEY[it.type])
                : (it.title || t("notifications.type_generic"));
              return (
                <div key={it.notification_id} className={`relative group ${it.read ? "" : "bg-[#FFFBEA]"}`}>
                  <button
                    data-testid={`notif-item-${it.notification_id}`}
                    onClick={() => openItem(it)}
                    className="w-full flex gap-3 px-3 py-3 pr-10 text-left hover:bg-[#FAFAFA] transition-colors"
                  >
                  <div className="w-10 h-12 bg-[#F5F5F5] shrink-0 overflow-hidden">
                    {it.target_thumbnail_url ? (
                      <img src={it.target_thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon size={16} className="text-[#999]" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Icon size={12} className="text-[#666] shrink-0" />
                      <p className="text-[11px] font-semibold text-[#0A0A0A] uppercase tracking-wide truncate">{label}</p>
                      {!it.read && <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />}
                    </div>
                    <p className="text-xs text-[#333] mt-0.5 line-clamp-2">{
                      it.type === "FOLLOW_REQUEST"
                        ? t("notifications.follow_request_msg", { name: it.meta?.display_name || it.target_title || "Jemand", defaultValue: it.message })
                        : it.message
                    }</p>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-[#999] truncate max-w-[65%]">{it.target_title || ""}</span>
                      <span className="text-[10px] text-[#999]">{relativeTimeT(it.created_at, t)}</span>
                    </div>
                  </div>
                </button>
                  <button
                    data-testid={`notif-delete-${it.notification_id}`}
                    onClick={(e) => deleteOne(e, it)}
                    aria-label={t("notifications.delete_aria")}
                    className="absolute top-2 right-2 p-1 rounded-full text-[#999] hover:text-red-500 hover:bg-white opacity-0 group-hover:opacity-100 transition"
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="border-t border-[#E5E5E5] px-4 py-2 bg-[#FAFAFA]">
          <button
            data-testid="notif-open-watchlist"
            onClick={() => { onClose?.(); navigate("/watchlist"); }}
            className="w-full text-xs text-[#0A0A0A] font-medium hover:underline"
          >
            {t("notifications.goto_watchlist")}
          </button>
        </div>
      )}
    </div>
  );
}
